import { Phone } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import moment from "moment";
import pluralize from "pluralize";
import { ReactNode } from "react";
import get from "lodash/fp/get";
import { Toggle } from "react-powerplug";
import { IGetGovernmentQuery } from "../../lib/queries/__generated__/GetGovernmentQuery.generated";
import DelayedHover from "../DelayedHover";
import Container from "../layout/Container";
import AttachmentsToolbarIcon from "../project/icons/AttachmentsToolbarIcon";
import GovernmentsToolbarIcon from "../project/icons/GovernmentsInvolvedToolbarIcon";
import LinksToolbarIcon from "../project/icons/LinksToolbarIcon";
import MissionAccomplishedIcon from "../project/icons/MissionAccomplishedIcon";
import NewsworthyToolbarIcon from "../project/icons/NewsworthyToolbarIcon";
import ProductsToolbarIcon from "../project/icons/ProductsToolbarIcon";
import SadFaceIcon from "../project/icons/SadFaceIcon";
import VendorsToolbarIcon from "../project/icons/VendorsToolbarIcon";
import { Margin } from "../spacings";
import ProfilePicture from "@govlaunch/profile-picture";
import { getGovlaunchUrl } from "../utils/getGovlaunchUrl";

interface IProjectsProps {
  government: NonNullable<IGetGovernmentQuery["government"]>;
}

export default function Projects({ government }: IProjectsProps) {
  const projects = government.projects?.items || [];
  const DEFAULT_PAGE_ITEMS = 4;

  return (
    <Phone>
      {(isPhone: boolean) => (
        <Container>
          <div
            css={{
              backgroundColor: palette.white,
              borderRadius: 5,
              border: `solid 1px ${palette.lightestGray}`
            }}
          >
            <div
              css={{
                padding: 15,
                borderBottom: `solid 1px ${palette.lightestGray}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <span
                css={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: palette.sealBlue,
                  textTransform: "uppercase"
                }}
              >
                Projects ({projects.length})
              </span>
            </div>

            <Toggle>
              {({ on: viewAll, toggle: toggleViewAll }) => (
                <>
                  <div
                    css={{
                      display: "grid",
                      gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
                      gridColumnGap: 16,
                      gridRowGap: 16,
                      padding: 16
                    }}
                  >
                    {projects.slice(0, viewAll ? projects.length : DEFAULT_PAGE_ITEMS).map((project) => {
                      return (
                        <a
                          key={project.slug}
                          href={`${getGovlaunchUrl()}/projects/${project.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          css={{
                            textDecoration: "none",
                            color: "#000",
                            cursor: "pointer"
                          }}
                        >
                          <Project project={project} />
                        </a>
                      );
                    })}
                  </div>

                  {projects.length > DEFAULT_PAGE_ITEMS && !viewAll && (
                    <div
                      role="button"
                      css={{
                        borderTop: `solid 1px ${palette.lightestGray}`,
                        padding: 15,
                        textAlign: "center",
                        fontSize: 12,
                        color: palette.sealBlue,
                        letterSpacing: 0.5,
                        fontWeight: 500,
                        cursor: "pointer"
                      }}
                      onClick={toggleViewAll}
                    >
                      VIEW ALL
                    </div>
                  )}
                </>
              )}
            </Toggle>
          </div>
        </Container>
      )}
    </Phone>
  );
}

interface IProjectProps {
  project: NonNullable<NonNullable<NonNullable<IGetGovernmentQuery["government"]>["projects"]>["items"]>[0];
}

function Project({ project }: IProjectProps) {
  return (
    <div
      css={{
        padding: 16,
        borderRadius: 12,
        boxShadow: "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
        backgroundColor: palette.white,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "box-shadow .25s",
        "&:hover": {
          boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2)"
        }
      }}
    >
      <div
        css={{
          display: "grid",
          alignItems: "center",
          gridTemplateColumns: "46px 1fr",
          gridColumnGap: 16
        }}
      >
        {project.icon ? (
          <div
            css={{
              width: 46,
              height: 46,
              backgroundImage: project.icon ? `url(${project.icon || ""})` : undefined,
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain"
            }}
          />
        ) : (
          <div>
            <ProfilePicture
              size={46}
              image={
                project.__typename === "GovernmentProject" && project.government
                  ? get("government.logo", project)
                  : get("group.logo", project)
              }
              name={
                project.__typename === "GovernmentProject" && project.government
                  ? get("government.name", project)
                  : get("group.name", project)
              }
              css={{
                gridArea: "icon"
              }}
            />
          </div>
        )}

        <h2
          css={{
            fontSize: 16,
            fontWeight: 500,
            margin: 0,
            lineHeight: 1.2,
            wordBreak: "break-word"
          }}
        >
          {project.title}
        </h2>
      </div>

      {project.subtitle && <div>{project.subtitle}</div>}

      <div
        css={{
          marginTop: 16
        }}
      >
        <p>{project.description}</p>
      </div>

      <div
        css={{
          marginTop: 16,
          display: "flex"
        }}
      >
        {project.tags.length > 1 ? (
          <>
            <span
              css={{
                padding: "5px 10px",
                backgroundColor: "rgba(219, 236, 247, 0.5)",
                borderTopLeftRadius: 12.5,
                borderBottomLeftRadius: 12.5,
                fontSize: 12,
                color: palette.text,
                display: "inline-flex"
              }}
            >
              {project.tags[0].name}
            </span>

            <span
              css={{
                padding: "5px 10px",
                backgroundColor: "#48A0DA",
                borderTopRightRadius: 12.5,
                borderBottomRightRadius: 12.5,
                fontSize: 10,
                display: "inline-flex",
                color: palette.white,
                fontWeight: "bold",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              +{project.tags.length - 1}
            </span>
          </>
        ) : (
          <span
            css={{
              padding: "5px 10px",
              backgroundColor: "rgba(219, 236, 247, 0.5)",
              borderRadius: 12.5,
              fontSize: 12,
              color: palette.text,
              display: "inline-flex"
            }}
          >
            {project.tags[0].name}
          </span>
        )}
      </div>

      {project.outcome && (
        <Margin mt={16}>
          {project.outcome === "success" && (
            <div
              css={{
                display: "grid",
                gridAutoColumns: "max-content",
                gridAutoFlow: "column",
                gridColumnGap: 12,
                alignItems: "center"
              }}
            >
              <MissionAccomplishedIcon width={26} height={26} />

              <strong
                css={{
                  fontSize: 13,
                  lineHeight: `${13 * 1.33}px`,
                  color: "#178C62"
                }}
              >
                Project was a Success
              </strong>
            </div>
          )}

          {project.outcome === "failed" && (
            <div
              css={{
                display: "grid",
                gridAutoColumns: "max-content",
                gridAutoFlow: "column",
                gridColumnGap: 12,
                alignItems: "center"
              }}
            >
              <SadFaceIcon width={26} height={26} />

              <strong
                css={{
                  fontSize: 14,
                  lineHeight: `${13 * 1.33}px`,
                  color: "#8F2020"
                }}
              >
                Project was a Failure
              </strong>
            </div>
          )}
        </Margin>
      )}

      {hasElements(project) && (
        <Margin mt={16}>
          <div
            css={{
              display: "flex",
              alignItems: "center",
              "> div:not(:first-of-type)": {
                marginLeft: 16
              }
            }}
          >
            {project.itemsCount.products > 0 && (
              <WithCounter
                count={project.itemsCount.products}
                tooltip={pluralize("Product", project.itemsCount.products, true)}
              >
                <ProductsToolbarIcon />
              </WithCounter>
            )}

            {project.itemsCount.vendors > 0 && (
              <WithCounter
                count={project.itemsCount.vendors}
                tooltip={pluralize("Vendors", project.itemsCount.vendors, true)}
              >
                <VendorsToolbarIcon />
              </WithCounter>
            )}

            {project.itemsCount.newsworthy > 0 && (
              <WithCounter
                count={project.itemsCount.newsworthy}
                tooltip={pluralize("Newsworthy", project.itemsCount.newsworthy, true)}
              >
                <NewsworthyToolbarIcon />
              </WithCounter>
            )}

            {project.itemsCount.links > 0 && (
              <WithCounter
                count={project.itemsCount.links}
                tooltip={pluralize("Links", project.itemsCount.links, true)}
              >
                <LinksToolbarIcon />
              </WithCounter>
            )}

            {project.itemsCount.attachments > 0 && (
              <WithCounter
                count={project.itemsCount.attachments}
                tooltip={pluralize("Attachments", project.itemsCount.attachments, true)}
              >
                <AttachmentsToolbarIcon />
              </WithCounter>
            )}

            {project.itemsCount.locations > 0 && (
              <WithCounter
                count={project.itemsCount.locations}
                tooltip={`${pluralize("Governments", project.itemsCount.locations, true)} Involved`}
              >
                <GovernmentsToolbarIcon />
              </WithCounter>
            )}
          </div>
        </Margin>
      )}

      <time
        css={{
          marginLeft: "auto",
          marginTop: "auto",
          fontSize: 12,
          color: palette.mediumGray
        }}
      >
        {moment(project.createdAt).fromNow()}
      </time>
    </div>
  );
}

function hasElements(
  project: NonNullable<NonNullable<NonNullable<IGetGovernmentQuery["government"]>["projects"]>["items"]>[0]
) {
  return (
    project.products.length > 0 ||
    project.newsworthy.length > 0 ||
    project.links.length > 0 ||
    project.attachments.length > 0 ||
    project.locations.length > 0
  );
}

interface IWithCounterProps {
  children: ReactNode;
  tooltip: ReactNode;
  count: number;
}
function WithCounter({ children, count, tooltip }: IWithCounterProps) {
  return (
    <DelayedHover ms={1}>
      {({ hover, ...bind }) => (
        <Popover
          isOpen={hover}
          placement="top"
          arrowSize={6}
          render={() => {
            return (
              <div
                css={{
                  padding: 6,
                  color: palette.white,
                  fontSize: 10,
                  fontWeight: "bold"
                }}
              >
                {tooltip}
              </div>
            );
          }}
          borderColor="transparent"
          backgroundColor="#333333"
          boxStyle={{
            boxShadow: "0 2px 12px 0 rgba(0, 0, 0, 0.2)",
            zIndex: 9999
          }}
        >
          {({ innerRef }: { innerRef: any }) => (
            <div
              {...bind}
              ref={innerRef}
              css={{
                position: "relative",
                display: "inline-flex"
              }}
            >
              {children}

              <span
                css={{
                  position: "absolute",
                  backgroundColor: palette.innovatorBlue,
                  width: 16,
                  height: 16,
                  left: "50%",
                  top: "50%",
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: palette.white,
                  borderRadius: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {count > 9 ? "9+" : count}
              </span>
            </div>
          )}
        </Popover>
      )}
    </DelayedHover>
  );
}
